import { Injectable, inject } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { InvestigationDashboardPreferencesEntity } from '../../domain/entities/investigation-dashboard-preferences.entity';
import { InvestigationDashboardWidgetDataEntity } from '../../domain/entities/investigation-dashboard-widget-data.entity';
import { InvestigationDashboardWidgetEntity } from '../../domain/entities/investigation-dashboard-widget.entity';
import { DashboardInvestigationRowEntity } from '../../domain/entities/dashboard-investigation-row.entity';
import { InvestigationStatusSnapshotEntity } from '../../domain/entities/investigation-status-snapshot.entity';
import { InvestigationPrioritySnapshotEntity } from '../../domain/entities/investigation-priority-snapshot.entity';
import { InvestigationDashboardTimeframe } from '../../domain/enums/investigation-dashboard-timeframe.enum';
import { InvestigationDashboardApi } from '../api/investigation-dashboard.api';
import { InvestigationsApi } from '../api/investigations.api';
import { UpdateInvestigationDashboardPreferencesRequestDto } from '../dtos/update-investigation-dashboard-preferences-request.dto';
import { InvestigationDashboardMapper } from '../mappers/investigation-dashboard.mapper';

@Injectable({ providedIn: 'root' })
export class InvestigationDashboardRepository {
  private readonly _dashboardApi = inject(InvestigationDashboardApi);
  private readonly _investigationsApi = inject(InvestigationsApi);
  private readonly _mapper = inject(InvestigationDashboardMapper);

  public getMe(): Observable<InvestigationDashboardPreferencesEntity> {
    return this._dashboardApi.getMe().pipe(map((dto) => this._mapper.toPreferences(dto)));
  }

  public getWidgets(): Observable<InvestigationDashboardWidgetEntity[]> {
    return this._dashboardApi.getWidgets().pipe(map((dtos) => this._mapper.toWidgetArray(dtos)));
  }

  public getWidgetData(
    widgetId: string,
    timeframe?: InvestigationDashboardTimeframe,
  ): Observable<InvestigationDashboardWidgetDataEntity> {
    return this._dashboardApi
      .getWidgetData(widgetId, timeframe)
      .pipe(map((dto) => this._mapper.toWidgetData(dto)));
  }

  public updatePreferences(request: UpdateInvestigationDashboardPreferencesRequestDto): Observable<void> {
    return this._dashboardApi.updatePreferences(request);
  }

  public getDashboardInvestigations(params: HttpParams): Observable<{
    data: DashboardInvestigationRowEntity[];
    totalElements: number;
    totalPages: number;
  }> {
    return this._investigationsApi.getDashboardInvestigations(params).pipe(
      map((response) => ({
        data: this._mapper.toDashboardRowsDomain(response.data),
        totalElements: response.totalElements,
        totalPages: response.totalPages,
      })),
    );
  }

  public getStatusSnapshots(investigationId: string): Observable<InvestigationStatusSnapshotEntity[]> {
    return this._investigationsApi
      .getStatusSnapshots(investigationId)
      .pipe(map((dtos) => this._mapper.toStatusSnapshotArray(dtos)));
  }

  public getPrioritySnapshots(investigationId: string): Observable<InvestigationPrioritySnapshotEntity[]> {
    return this._investigationsApi
      .getPrioritySnapshots(investigationId)
      .pipe(map((dtos) => this._mapper.toPrioritySnapshotArray(dtos)));
  }

  public updateStatus(investigationId: string, statusId: string): Observable<void> {
    return this._investigationsApi.updateStatus(investigationId, { statusId });
  }

  public updatePriority(investigationId: string, priorityCode: string): Observable<void> {
    return this._investigationsApi.updatePriority(investigationId, { priorityCode });
  }

  public updateAssignees(
    investigationId: string,
    primaryAssigneeUserId: string | null,
    secondaryAssigneeUserId: string | null,
  ): Observable<void> {
    return this._investigationsApi.updateAssignees(investigationId, {
      primaryAssigneeUserId,
      secondaryAssigneeUserId,
    });
  }

  public setWatchInvestigation(investigationId: string, isWatching: boolean): Observable<void> {
    return this._investigationsApi.setWatchInvestigation(investigationId, isWatching);
  }
}
